// Primitivas de zona horaria del módulo de tiempo. PRIVADO: no se reexporta en
// `index.ts`; las funciones públicas fijan la zona con `businessZone()`.

export interface ZonedParts {
  y: number;
  m: number;
  d: number;
  /** Hora 0 a 23. */
  h: number;
  min: number;
  s: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** Componentes del instante en la zona; null si el instante es inválido. */
export function zonedParts(instant: Date, timeZone: string): ZonedParts | null {
  if (Number.isNaN(instant.getTime())) {
    return null;
  }
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (t: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === t)?.value ?? '0');
  // Algunos ICU emiten "24" a medianoche aun con h23.
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    h: get('hour') % 24,
    min: get('minute'),
    s: get('second'),
  };
}

function offsetMinutes(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  if (p === null) {
    return 0;
  }
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
  return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** Instante UTC del inicio del día local (año, mes, día) en la zona: su medianoche, o
 *  el primer instante del día si el horario de verano se salta la medianoche. */
export function zonedMidnight(y: number, m: number, d: number, timeZone: string): Date {
  const guess = Date.UTC(y, m - 1, d);
  const first = guess - offsetMinutes(new Date(guess), timeZone) * 60_000;
  const midnight = new Date(guess - offsetMinutes(new Date(first), timeZone) * 60_000);
  // Si la medianoche no existe (00:00 salta a 01:00), el cálculo cae en el día anterior;
  // entonces el día empieza en `first`, el instante del salto.
  return zonedParts(midnight, timeZone)?.d === d ? midnight : new Date(first);
}
